import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import api from "../../api";
import TraceabilityModal from "./TraceabilityModal";
import VoiceConversationMode from "../../components/VoiceConversationMode";
import { Phone, MessageSquare } from 'lucide-react';

export default function ChatPage() {
  const { t, i18n } = useTranslation();
  const { presenceId } = useParams();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, traceId?, audio?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [traceId, setTraceId] = useState(null);
  const messagesEndRef = useRef(null);
  const [presenceTyping, setPresenceTyping] = useState(false);
  
  // Audio states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const recordingTimerRef = useRef(null);
  const audioRef = useRef(null);
  
  // Voice conversation mode state
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [presenceName, setPresenceName] = useState('');
  const [presenceGender, setPresenceGender] = useState('neutral');

  // Fetch presence data for voice mode
  useEffect(() => {
    const fetchPresenceData = async () => {
      try {
        const res = await api.get(`/presence/${presenceId}`);
        setPresenceName(res.data.name);
        setPresenceGender(res.data.gender || 'neutral'); // 'male', 'female', or 'neutral'
      } catch (err) {
        console.error('Error fetching presence:', err);
      }
    };
    
    if (presenceId) {
      fetchPresenceData();
    }
  }, [presenceId]);

  // Yeni mesaj geldiğinde otomatik scroll
  useEffect(() => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput("");

    // Kullanıcı mesajını hemen göster
    setMessages((prev) => [
      ...prev,
      { role: "user", content: userMessage, _localId: Date.now() + "-u" },
    ]);

    setLoading(true);
    setPresenceTyping(true);

    try {
      const res = await api.post("/chat/send", {
        presenceId: presenceId,
        conversationId: conversationId,
        message: userMessage,
        language: i18n.language, // Kullanıcının dilini gönder
      });

      const data = res.data;
      setConversationId(data.conversationId);

      // Presence cevabı
      setMessages((prev) => [
        ...prev,
        {
          role: "presence",
          content: data.response,
          traceId: data.traceId,
          memories: data.memories,
          blocked: data.blocked,
          _localId: Date.now() + "-p",
        },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: t('presence.somethingWentWrong'),
          _localId: Date.now() + "-s",
        },
      ]);
    } finally {
      setLoading(false);
      // küçük bir delay ile typing'i kapat (daha doğal his)
      setTimeout(() => setPresenceTyping(false), 300);
    }
  };

  // Audio Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        await handleAudioUpload(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioChunks([]);

      // Start timer (max 60 seconds)
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Microphone access denied:', err);
      alert(t('audio.microphoneError'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const handleAudioUpload = async (audioBlob) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', i18n.language);

      const res = await api.post('/audio/speech-to-text', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const transcribedText = res.data.text;
      
      // Set transcribed text to input
      setInput(transcribedText);
      
      // Optionally auto-send
      // await sendMessage() with transcribedText
      
    } catch (err) {
      console.error('Speech-to-text error:', err);
      alert(t('audio.transcriptionError'));
    } finally {
      setLoading(false);
    }
  };

  // Generate TTS for assistant response
  const generateTTS = async (text) => {
    try {
      const res = await api.post('/audio/text-to-speech', {
        text: text,
        voice: 'alloy', // Default voice
        speed: 1.0,
        language: i18n.language
      }, {
        responseType: 'blob'
      });

      const audioUrl = URL.createObjectURL(res.data);
      return audioUrl;
    } catch (err) {
      console.error('TTS error:', err);
      return null;
    }
  };

  // Play audio automatically (based on settings)
  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(err => console.error('Audio play error:', err));
    }
  };

  // Update sendMessage to include TTS
  const sendMessageWithAudio = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput("");

    // Kullanıcı mesajını hemen göster
    setMessages((prev) => [
      ...prev,
      { role: "user", content: userMessage, _localId: Date.now() + "-u" },
    ]);

    setLoading(true);
    setPresenceTyping(true);

    try {
      const res = await api.post("/chat/send", {
        presenceId: presenceId,
        conversationId: conversationId,
        message: userMessage,
        language: i18n.language,
      });

      const data = res.data;
      setConversationId(data.conversationId);

      // Generate TTS for response
      const audioUrl = await generateTTS(data.response);

      // Presence cevabı (with audio)
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          traceId: data.trace_id || null,
          audio: audioUrl,
          _localId: Date.now() + "-a",
        },
      ]);

      // Auto-play if enabled
      if (audioUrl) {
        playAudio(audioUrl);
      }

    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: t('presence.somethingWentWrong'),
          _localId: Date.now() + "-s",
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => setPresenceTyping(false), 300);
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Voice Conversation Mode Overlay */}
      {isVoiceMode && (
        <VoiceConversationMode
          presenceId={presenceId}
          presenceName={presenceName}
          presenceGender={presenceGender}
          token={localStorage.getItem('token')}
          language={i18n.language}
          onClose={() => setIsVoiceMode(false)}
        />
      )}

      <div className="h-full flex flex-col lg:flex-row overflow-hidden">
        {/* Main chat column */}
        <section className="flex-1 flex flex-col bg-gradient-to-b from-slate-950 via-slate-950/95 to-slate-950/80 backdrop-blur relative">
          {/* hafif gradient glow overlay */}
          <div className="pointer-events-none absolute inset-x-16 -top-32 h-48 bg-cyan-500/10 blur-3xl" />

          {/* Header - Fixed */}
          <div className="relative px-3 lg:px-4 py-2 lg:py-3 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] lg:text-xs uppercase tracking-[0.2em] text-cyan-400/70">
                {t('presence.conversation')}
              </div>
              <div className="text-xs lg:text-sm text-slate-300 flex items-center gap-2 truncate">
                <span className="relative inline-flex h-2 lg:h-2.5 w-2 lg:w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 lg:h-2.5 w-2 lg:w-2.5 bg-emerald-400"></span>
                </span>
                <span className="truncate">{t('presence.liveWith')} {t('presence.presenceId')}{presenceId.slice(0, 6)}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Session ID */}
              {conversationId && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hidden lg:block text-[11px] px-2 py-1 rounded-full bg-slate-900/80 border border-slate-700 text-slate-400"
                >
                  {t('presence.session')}: {conversationId.slice(0, 8)}…
                </motion.div>
              )}

              {/* Voice Mode Toggle Button */}
              <button
                onClick={() => setIsVoiceMode(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                title="Sesli Konuşma Modu"
              >
                <Phone size={18} className="text-white" />
                <span className="hidden sm:inline text-white font-medium text-sm">Sesli Mod</span>
              </button>
            </div>
          </div>

        {/* Messages */}
        <div className="relative flex-1 overflow-y-auto px-3 lg:px-4 py-3 lg:py-4 space-y-2 lg:space-y-3">
          <AnimatePresence initial={false}>
            {messages.length === 0 && !loading && (
              <motion.div
                key="empty-state"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.6, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="text-sm text-slate-500 text-center mt-12"
              >
                {t('presence.emptyChat')}
              </motion.div>
            )}

            {messages.map((msg) => (
              <MessageBubble
                key={msg._localId}
                message={msg}
                onShowTrace={() => msg.traceId && setTraceId(msg.traceId)}
              />
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          <AnimatePresence>
            {presenceTyping && (
              <TypingIndicator key="typing-indicator" />
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} />
        </div>

        {/* Input - Fixed at bottom */}
        <div className="relative border-t border-slate-800 px-3 lg:px-4 py-2 lg:py-3 bg-slate-950/95 backdrop-blur flex-shrink-0">
          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-0 left-0 right-0 bg-red-500/10 border-b border-red-500/30 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <span className="text-xs text-red-400 font-medium">
                  {t('audio.recording')} {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <button
                onClick={stopRecording}
                className="text-xs text-red-400 hover:text-red-300 font-medium"
              >
                {t('audio.stopRecording')}
              </button>
            </div>
          )}

          <motion.div
            initial={false}
            animate={{ 
              boxShadow: loading ? "0 0 18px rgba(34,211,238,0.35)" : isRecording ? "0 0 18px rgba(239,68,68,0.35)" : "0 0 0 rgba(0,0,0,0)"
            }}
            className="flex items-end gap-2 rounded-xl lg:rounded-2xl bg-slate-900/80 border border-slate-700 px-2 lg:px-3 py-2"
          >
            {/* Microphone button */}
            <motion.button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              whileTap={{ scale: 0.9 }}
              whileHover={!loading ? { scale: 1.05 } : {}}
              className={`h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-full text-lg disabled:opacity-40 disabled:cursor-not-allowed transition ${
                isRecording
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-slate-800 text-cyan-400 hover:bg-slate-700'
              }`}
              title={isRecording ? t('audio.stopRecording') : t('audio.startRecording')}
            >
              {isRecording ? '⏹' : '🎤'}
            </motion.button>

            <textarea
              className="flex-1 resize-none bg-transparent border-0 focus:ring-0 focus:outline-none text-xs lg:text-sm text-slate-100 placeholder:text-slate-500"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isRecording
                  ? t('audio.listening')
                  : loading
                  ? t('presence.presenceThinking')
                  : t('presence.askSomething')
              }
              disabled={isRecording}
            />
            
            <motion.button
              onClick={sendMessageWithAudio}
              disabled={loading || !input.trim() || isRecording}
              whileTap={{ scale: 0.9 }}
              whileHover={!loading && input.trim() ? { scale: 1.05 } : {}}
              className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-full bg-cyan-400 text-slate-900 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_12px_rgba(34,211,238,0.7)] hover:shadow-[0_0_20px_rgba(34,211,238,0.95)] transition"
            >
              {loading ? (
                <span className="animate-spin">⟳</span>
              ) : (
                "➤"
              )}
            </motion.button>
          </motion.div>
          
          {/* Hidden audio player for TTS */}
          <audio ref={audioRef} className="hidden" />
        </div>
      </section>

      {/* Right Panel: Simulation Lab Monitor */}
      <aside className="w-80 hidden xl:flex flex-col gap-3">
        {/* Session Stats */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="border border-slate-800 rounded-xl bg-slate-900/70 p-3"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-2">
            📊 Session Monitor
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-slate-500">Messages</div>
              <div className="text-cyan-300 font-semibold text-base">{messages.length}</div>
            </div>
            <div>
              <div className="text-slate-500">Conv ID</div>
              <div className="text-slate-300 font-mono text-[10px]">
                {conversationId ? conversationId.slice(0, 8) : '—'}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Last Response Analysis */}
        {messages.length > 0 && messages[messages.length - 1].role === 'presence' && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-slate-800 rounded-xl bg-slate-900/70 p-3"
          >
            <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-2">
              🧠 Last Response
            </div>
            {messages[messages.length - 1].memories && (
              <div className="mb-2">
                <div className="text-[11px] text-slate-400">Memories Used</div>
                <div className="text-emerald-300 font-semibold">
                  {messages[messages.length - 1].memories.length || 0} injected
                </div>
              </div>
            )}
            {messages[messages.length - 1].blocked && (
              <div className="px-2 py-1 rounded bg-amber-500/10 border border-amber-400/40 text-amber-300 text-[11px]">
                ⚠️ Governance Filter Active
              </div>
            )}
            {messages[messages.length - 1].traceId && (
              <button
                onClick={() => setTraceId(messages[messages.length - 1].traceId)}
                className="mt-2 w-full text-[11px] px-2 py-1 rounded bg-cyan-500/10 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/20 transition"
              >
                🔍 View Traceability
              </button>
            )}
          </motion.div>
        )}

        {/* Lab Hints */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="border border-slate-800 rounded-xl bg-slate-900/70 p-3 text-xs text-slate-400"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-2">
            💡 Lab Mode
          </div>
          <p className="mb-2">
            Test persona, memory injection, and governance in real-time.
          </p>
          <div className="space-y-1 text-[11px]">
            <div>• Click <span className="text-cyan-300">Traceability ↗</span> to see memory usage</div>
            <div>• Watch governance filters activate</div>
            <div>• Monitor persona behavior</div>
          </div>
        </motion.div>
      </aside>

      <AnimatePresence>
        {traceId && (
          <TraceabilityModal traceId={traceId} onClose={() => setTraceId(null)} />
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

function MessageBubble({ message, onShowTrace }) {
  const { t } = useTranslation();
  const isUser = message.role === "user";
  const isPresence = message.role === "presence";
  const isSystem = message.role === "system";

  const alignment = isUser ? "items-end" : "items-start";
  const bubbleClasses = isUser
    ? "bg-slate-800 text-slate-50 border border-slate-700"
    : isPresence
    ? "bg-cyan-400/10 border border-cyan-400/40 text-cyan-50"
    : "bg-amber-500/10 border border-amber-400/60 text-amber-50";

  const fromX = isUser ? 20 : -20;

  return (
    <motion.div
      className={`flex ${alignment}`}
      initial={{ opacity: 0, x: fromX, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: fromX / 2, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 260, damping: 22, mass: 0.7 }}
      layout
    >
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm relative ${bubbleClasses}`}
      >
        {/* hafif gradient overlay sadece presence için */}
        {isPresence && (
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 via-transparent to-cyan-300/8" />
        )}

        <div className="relative whitespace-pre-wrap">{message.content}</div>

        {isPresence && (
          <div className="relative mt-2 space-y-1.5">
            {/* Audio player */}
            {message.audio && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const audio = new Audio(message.audio);
                    audio.play();
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] bg-cyan-500/10 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/20 transition"
                >
                  <span>🔊</span>
                  {t('audio.playAudio')}
                </button>
              </div>
            )}

            {/* Indicators row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Memory badge */}
              {message.memories && message.memories.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 border border-emerald-400/40 text-emerald-300">
                  <span>🧠</span>
                  {message.memories.length} {message.memories.length === 1 ? 'memory' : 'memories'}
                </span>
              )}
              
              {/* Blocked/Safe mode badge */}
              {message.blocked && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 border border-amber-400/40 text-amber-300">
                  <span>⚠️</span>
                  {t('presence.safeModeResponse')}
                </span>
              )}
            </div>

            {/* Traceability link */}
            {message.traceId && (
              <motion.button
                type="button"
                onClick={onShowTrace}
                whileHover={{ scale: 1.02 }}
                className="text-[11px] text-cyan-200/80 hover:text-cyan-50 flex items-center gap-1"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" />
                {t('presence.traceability')} ↗
              </motion.button>
            )}
          </div>
        )}

        {isSystem && (
          <div className="mt-1 text-[10px] text-amber-200/80">
            {t('presence.systemMessage')}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  const { t } = useTranslation();
  return (
    <motion.div
      className="flex items-start"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
    >
      <div className="inline-flex items-center gap-1 rounded-full bg-cyan-400/10 border border-cyan-400/40 px-3 py-1.5 text-xs text-cyan-50">
        <span className="h-2 w-2 rounded-full bg-cyan-300 animate-pulse" />
        <span className="flex gap-0.5">
          <Dot />
          <Dot delay={0.1} />
          <Dot delay={0.2} />
        </span>
        <span className="text-[11px] text-cyan-100/80 ml-1">
          {t('presence.presenceThinking')}
        </span>
      </div>
    </motion.div>
  );
}

function Dot({ delay = 0 }) {
  return (
    <motion.span
      className="h-1.5 w-1.5 rounded-full bg-cyan-200"
      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
      transition={{
        duration: 0.9,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
    />
  );
}
